import { CopyBlock } from 'react-code-blocks';
import '../DocumentationPages.css';
import accessibleCodeTheme from '../accessibleCodeTheme';
const GeoserverInfoView = () => {
  return (
    <div
      style={{
        backgroundColor: '#CEDBD6',
        backgroundSize: 'cover',
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',

        padding: '35px 15% 35px 10%',
      }}
    >
      <h3>
        Using California Wildfire Commons' GeoServer Services in ArcGIS, Python,
        and Web Mapping Tools
      </h3>
      The GeoServer instance of the California Wildfire Commons offers a range
      of OGC-compliant web services—such as Web Map Service (WMS), Web Coverage
      Service (WCS), and Web Feature Service (WFS)—that facilitate smooth access
      to the Commons' resources across different platforms. This document
      outlines detailed workflows for incorporating these services into ArcGIS,
      Python for scripting and analysis, and JavaScript libraries (like Leaflet
      and OpenLayers) for web-based mapping. It also includes techniques for
      processing downloaded GeoTIFF raster data and GeoJSON vector files in
      these environments.
      <hr />
      <ol>
        <li>
          <strong>Using WMS in ArcGIS</strong>
        Adding a WMS Layer
        <ol>
          <li>Open ArcGIS Pro or ArcMap.</li>
          <li>
            Go to the "Catalog" pane (If it's not visible, enable it via{' '}
            <strong>
              View {'> '}
              Catalog Pane
            </strong>
            ).
          </li>
          <li>
            Right-click on <strong>"Servers"</strong> and select{' '}
            <strong>"Add WMS Server"</strong>.
          </li>
          <li>
            Enter the WMS URL: <u>https://sparcal.sdsc.edu/geoserver/rrk/wms</u>
          </li>
          <li>
            Click <strong>"OK"</strong> to add the WMS service.
          </li>
          <li>
            Expand the added WMS service, locate the{' '}
            <u>rrk:frid_timesincelastfire_2022_202401_t1_v5</u> layer.
          </li>
          <li>
            Drag the layer onto the map or right-click and select "Add to
            Current Map".
          </li>
        </ol>
        This should successfully add the WMS layer to your ArcGIS map.
        <hr />
        </li>
        <li>
          <strong>Using WMS in Leaflet</strong>
        <strong>Prerequisites</strong>
        <ul>
          <li>Include Leaflet JavaScript and CSS</li>
          <li>Have a valid WMS service URL</li>
        </ul>
        <strong>HTML Setup</strong>
        <div className="code-wrapper">
          <CopyBlock
            text={`<!DOCTYPE html>
  <html>
  <head>
      <link rel="stylesheet"  
            href="https://unpkg.com/leaflet@1.7.1/dist/leaflet.css" />
      <script src="https://unpkg.com/leaflet@1.7.1/dist/leaflet.js"></script>
  </head>
  <body>
      <div id="map" style="height: 600px;"></div>
  </body>
  </html>`}
            language={'javascript'}
            showLineNumbers={false}
            theme={accessibleCodeTheme}
            wrapLines
            customStyle={{ background: '#ffffff', borderRadius: '4px', padding: '1em' }}
            style={{ margin: '20px' }}
          />
        </div>
        <strong>JavaScript Implementation</strong>
        <div className="code-wrapper">
          <CopyBlock
            text={`// Initialize the map
  var map = L.map('map').setView([39.8, -98.5], 4);
  
  // Add base tile layer (optional)
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '(c) OpenStreetMap contributors'
  }).addTo(map);
  
  // Add WMS Layer
  var wmsLayer = L.tileLayer.wms('https://sparcal.sdsc.edu/geoserver/rrk/wms', {
      layers: 'rrk:frid_timesincelastfire_2022_202401_t1_v5',
      format: 'image/png',
      transparent: true,
      attribution: 'Your Data Source'
  }).addTo(map);`}
            language={'javascript'}
            showLineNumbers={false}
            theme={accessibleCodeTheme}
            wrapLines
            customStyle={{ background: '#ffffff', borderRadius: '4px', padding: '1em' }}
          />
        </div>
        {/* add iamge */}
        <hr />
        </li>
        <li>
          <strong>Using WMS in OpenLayers</strong>
        <strong>Prerequisites</strong>
        <ul>
          <li>Include OpenLayers library</li>
          <li>Have a valid WMS service URL</li>
        </ul>
        <strong>HTML Setup</strong>{' '}
        <div className="code-wrapper">
          <CopyBlock
            text={`<!DOCTYPE html>
  <html>
  <head>
      <link rel="stylesheet" 
            href="https://cdn.jsdelivr.net/npm/ol@v7.1.0/ol.css">
      <script src="https://cdn.jsdelivr.net/npm/ol@v7.1.0/dist/ol.js"></script>
  </head>
  <body>
      <div id="map" style="height: 600px;"></div>
  </body>
  </html>`}
            language={'javascript'}
            showLineNumbers={false}
            theme={accessibleCodeTheme}
            wrapLines
            customStyle={{ background: '#ffffff', borderRadius: '4px', padding: '1em' }}
          />
        </div>
        {/* add image */}
        <strong>JavaScript Implementation</strong>{' '}
        <div className="code-wrapper">
          <CopyBlock
            text={`// Create a base map
  var map = new ol.Map({
      target: 'map',
      layers: [
          // Base map layer
          new ol.layer.Tile({
              source: new ol.source.OSM()
          }),
          // WMS Layer
          new ol.layer.Tile({
              source: new ol.source.TileWMS({
                  url: 'https://sparcal.sdsc.edu/geoserver/rrk/wms',
                  params: {
                      'LAYERS': 'rrk:frid_timesincelastfire_2022_202401_t1_v5',
                      'TILED': true,
                      'TRANSPARENT': true
                  },
                  serverType: 'geoserver'
              })
          })
      ],
      view: new ol.View({
          center: ol.proj.fromLonLat([-98.5, 39.8]),
          zoom: 4
      })
  });`}
            language={'javascript'}
            showLineNumbers={false}
            theme={accessibleCodeTheme}
            wrapLines
            customStyle={{ background: '#ffffff', borderRadius: '4px', padding: '1em' }}
          />
        </div>
        {/* add iamge */}
        <hr />
        </li>
        <li>
          <strong>Using WMS in ArcGIS JavaScript API</strong>
        <strong>
          <strong>Prerequisites</strong>
        </strong>
        <ul>
          <li>Include ArcGIS JavaScript API</li>
          <li>Have a valid WMS service URL</li>
        </ul>
        <strong>HTML Setup</strong>{' '}
        <div className="code-wrapper">
          <CopyBlock
            text={`<!DOCTYPE html>
  <html>
  <head>
      <link rel="stylesheet"   
            href="https://js.arcgis.com/4.28/esri/themes/light/main.css">
      <script src="https://js.arcgis.com/4.28/"></script>
  </head>
  <body>
      <div id="viewDiv" style="height: 600px;"></div>
  </body>
  </html>`}
            language={'javascript'}
            showLineNumbers={false}
            theme={accessibleCodeTheme}
            wrapLines
            customStyle={{ background: '#ffffff', borderRadius: '4px', padding: '1em' }}
          />
        </div>
        {/* add image */}
        <strong>JavaScript Implementation</strong>{' '}
        <div className="code-wrapper">
          <CopyBlock
            text={`require([
      "esri/Map",
      "esri/views/MapView",
      "esri/layers/WMSLayer",
      "esri/layers/support/WMSSublayer"
  ], function(Map, MapView, WMSLayer, WMSSublayer) {
      // Create base map
      var map = new Map({
          basemap: "streets-navigation-vector"
      });
  
      // Create map view
      var view = new MapView({
          container: "viewDiv",
          map: map,
          center: [-98.5, 39.8],
          zoom: 4
      });
  
      // Create WMS Layer
      var wmsLayer = new WMSLayer({
          url: "https://sparcal.sdsc.edu/geoserver/rrk/wms",
          sublayers: [
              new WMSSublayer({
                  name: "rrk:frid_timesincelastfire_2022_202401_t1_v5"
              })
          ]
      });
  
      // Add WMS Layer to map
      map.add(wmsLayer);
  });`}
            language={'javascript'}
            showLineNumbers={false}
            theme={accessibleCodeTheme}
            wrapLines
            customStyle={{ background: '#ffffff', borderRadius: '4px', padding: '1em' }}
          />
        </div>
        {/* add iamge */}
        <hr />
        </li>
        <li>
          <strong>Using WMS in Python</strong>
        Use the OWSLib library to load WMS in Python:{' '}
        <div className="code-wrapper">
          <CopyBlock
            text={`from owslib.wms import WebMapService
  import matplotlib.pyplot as plt
  import requests
  from PIL import Image
  from io import BytesIO
  
  # Connect to WMS service
  wms_url = "https://sparcal.sdsc.edu/geoserver/rrk/wms"
  wms = WebMapService(wms_url)
  
  # Get an image of the layer
  layer = 'rrk:frid_timesincelastfire_2022_202401_t1_v5'
  bbox = (-124.5, 32.5, -113.5, 42.0)  # Example bounding box
  response = requests.get(wms.getmap(
      layers=[layer],
      srs='EPSG:4326',
      bbox=bbox,
      size=(800, 600),
      format='image/png',
      transparent=True
  ))
  
  # Display image
  img = Image.open(BytesIO(response.content))
  plt.imshow(img)
  plt.axis('off')
  plt.show()`}
            language={'python'}
            showLineNumbers={false}
            theme={accessibleCodeTheme}
            wrapLines
            customStyle={{ background: '#ffffff', borderRadius: '4px', padding: '1em' }}
          />
        </div>
        {/* add iamge */}
        </li>
        <li>
          <strong>Using WCS in ArcGIS Pro</strong>
        <ul>
          <li>Open ArcGIS Pro.</li>
          <li>
            Go to the "Catalog" pane (If it's not visible, enable it via{' '}
            <strong>
              View {'> '}
              Catalog Pane
            </strong>
            ).
          </li>{' '}
          <li>
            Right-click on <strong>"Servers"</strong> and select{' '}
            <strong>"Add WCS Server"</strong>.
          </li>
          <li>
            Enter the WCS URL: <u>https://sparcal.sdsc.edu/geoserver/rrk/wcs</u>
          </li>
          <li>
            Click <strong>"OK"</strong> to add the WCS service.
          </li>
          <li>
            Expand the added WCS service, locate the{' '}
            <u>rrk__frid_timesincelastfire_2022_202401_t1_v5</u> layer.
          </li>
          <li>
            Right-click the layer and select{' '}
            <strong>"Add to Current Map"</strong> to load it.
          </li>
        </ul>
        This should successfully add the WCS layer to your ArcGIS map.
        <hr />
        </li>
        <li>
          <strong>Using WCS in Python</strong>
        WCS allows downloading raster data in GeoTIFF format.
        <div className="code-wrapper">
          <CopyBlock
            text={`from owslib.wcs import WebCoverageService
  import rasterio
  
  wcs_url = "https://sparcal.sdsc.edu/geoserver/rrk/wcs"
  layer = "rrk__frid_timesincelastfire_2022_202401_t1_v5"
  
  # Connect to WCS
  wcs = WebCoverageService(wcs_url, version='1.0.0')
  
  # Get coverage data
  response = wcs.getCoverage(
      identifier=layer,
      bbox=(-124.5, 32.5, -113.5, 42.0),
      format='image/tiff',
      crs='EPSG:4326',
      width=800, height=600
  )
  
  # Save GeoTIFF file
  with open("output.tif", "wb") as f:
      f.write(response.read())
  
  # Read and display raster
  with rasterio.open("output.tif") as src:
      print(src.profile)  # Metadata of the raster`}
            language={'python'}
            showLineNumbers={false}
            theme={accessibleCodeTheme}
            wrapLines
            customStyle={{ background: '#ffffff', borderRadius: '4px', padding: '1em' }}
          />
        </div>
        {/* add iamge */}
        <hr />
        </li>
        <li>
          <strong>Using WFS in ArcGIS Pro</strong>
        <ul>
          <li>Open ArcGIS Pro.</li>
          <li>
            Go to the "Catalog" pane (If it's not visible, enable it via{' '}
            <strong>
              View{' > '}
              Catalog Pane
            </strong>
            ).
          </li>
          <li>
            Right-click on <strong>"Servers"</strong> and select{' '}
            <strong>"Add WFS Server"</strong>.
          </li>
          <li>
            Enter the WFS URL:{' '}
            <u>https://sparcal.sdsc.edu/geoserver/boundary/wfs</u>
          </li>
          <li>
            Click <strong>"OK"</strong> to add the WFS service.
          </li>
          <li>
            Expand the added WFS service, locate the{' '}
            <u>boundary:rrk_boundaries</u> layer.
          </li>
          <li>
            Right-click the layer and select{' '}
            <strong>"Add to Current Map"</strong> to load it.
          </li>
        </ul>
        This should successfully add the WFS layer to your ArcGIS map.
        <hr />
        </li>
        <li>
          <strong>Using WFS in Python</strong>
        <div className="code-wrapper">
          <CopyBlock
            text={`import geopandas as gpd
  wfs_url = "https://sparcal.sdsc.edu/geoserver/boundary/wfs"
  layer = "boundary:rrk_boundaries"
  
  # Construct WFS request URL
  wfs_request = f"{wfs_url}?service=WFS&version=1.0.0&request=GetFeature&typeName={layer}&outputFormat=application/json"
  
  gdf = gpd.read_file(wfs_request)
  print(gdf.head())`}
            language={'python'}
            showLineNumbers={false}
            theme={accessibleCodeTheme}
            wrapLines
            customStyle={{ background: '#ffffff', borderRadius: '4px', padding: '1em' }}
          />
        </div>
        {/* add image */}
        <hr />
        </li>
        <li>
          <strong>Working with Downloaded GeoTIFF and GeoJSON</strong>
        Using a GeoTIFF File in Python
        <div className="code-wrapper">
          {' '}
          <CopyBlock
            text={`import rasterio
  import matplotlib.pyplot as plt
  
  # Read GeoTIFF file
  with rasterio.open("output.tif") as dataset:
      img = dataset.read(1)  # Read first band
      plt.imshow(img, cmap='viridis')
      plt.colorbar()
      plt.show()`}
            language={'python'}
            showLineNumbers={false}
            theme={accessibleCodeTheme}
            wrapLines
            customStyle={{ background: '#ffffff', borderRadius: '4px', padding: '1em' }}
          />
        </div>
        {/* add image */}Using a GeoJSON File in Python
        <div className="code-wrapper">
          <CopyBlock
            text={`import geopandas as gpd
  
  # Read GeoJSON file
  gdf = gpd.read_file("data.geojson")
  print(gdf.head())
  
  # Plot
  gdf.plot()
  plt.show()`}
            language={'python'}
            showLineNumbers={false}
            theme={accessibleCodeTheme}
            wrapLines
            customStyle={{ background: '#ffffff', borderRadius: '4px', padding: '1em' }}
          />
        </div>
        {/* add image */}
        Using a GeoJSON in ArcGIS Pro
        <ul>
          <li>
            <strong>Open ArcGIS Pro</strong>
          </li>
          <li>
            <strong> Navigate to the "Catalog" pane</strong> (If it's not
            visible, enable it via
            <strong> View</strong> {'>'} <strong>Catalog Pane</strong>)
          </li>
          <li>
            <strong>Right-click on the desired folder</strong> where you want to
            store the converted data
          </li>
          <li>
            <strong>Click "Add Data"</strong> and select your GeoJSON file
          </li>
          <li>
            <strong> Alternatively, use the "JSON To Features" tool:</strong>
            <ul>
              <li>
                Open the <strong>Geoprocessing pane</strong> (Analysis tab {'>'}{' '}
                Tools)
              </li>
              <li>
                Search for <strong>"JSON To Features"</strong>
              </li>
              <li>Input your GeoJSON file</li>
              <li>
                Select an <strong>output location</strong> (e.g., a geodatabase
                or shapefile)
              </li>
              <li>
                Click <strong>Run</strong>
              </li>
            </ul>
          </li>
        </ul>
        This will convert the GeoJSON into a feature class that ArcGIS Pro can
        work with.
        <hr />
        </li>
        <li>
          <strong>Using WPS in Python</strong>
        WPS allows executing geospatial processes such as cropping rasters or
        calculating zonal statistics. Below are examples using the CropCoverage
        and RasterZonalStatistics processes.
        <br />
        <strong>CropCoverage Example</strong>
        <br /> This example demonstrates how to use the CropCoverage WPS process
        to clip a raster layer to a specified bounding box.
        <div className="code-wrapper">
          {' '}
          <CopyBlock
            text={`from owslib.wps import WebProcessingService
  import rasterio
  import xml.etree.ElementTree as ET
  
  wps_url = "https://sparcal.sdsc.edu/geoserver/wps"
  layer = "rrk:frid_timesincelastfire_2022_202401_t1_v5"
  wps = WebProcessingService(wps_url, version='1.0.0')
  
  # Define inputs for CropCoverage
  inputs = [
      ('coverage', layer),  # Raster layer to crop
      ('bbox', '-124.5,32.5,-113.5,42.0'),  # Bounding box (minx, miny, maxx, maxy)
      ('crs', 'EPSG:4326')  # Coordinate reference system
  ]
  
  # Execute CropCoverage process
  execution = wps.execute('gs:CropCoverage', inputs, output=[('result', 'image/tiff')])
  
  # Wait for process to complete
  execution.wait()
  
  # Save output GeoTIFF
  output = execution.processOutputs[0].retrieveData()
  with open("cropped_output.tif", "wb") as f:
      f.write(output)
  
  # Read and verify raster
  with rasterio.open("cropped_output.tif") as src:
      print(src.profile)  # Metadata of the cropped raster
  
  `}
            language={'python'}
            showLineNumbers={false}
            theme={accessibleCodeTheme}
            wrapLines
            customStyle={{ background: '#ffffff', borderRadius: '4px', padding: '1em' }}
          />
        </div>
        {/* add image */}
        <strong>RasterZonalStatistics Example</strong>
        <br />
        This example demonstrates how to use the RasterZonalStatistics WPS
        process to calculate statistics for a raster within a vector geometry.
        <div className="code-wrapper">
          {' '}
          <CopyBlock
            text={`from owslib.wps import WebProcessingService
  import json
  import geopandas as gpd
  
  wps_url = "https://sparcal.sdsc.edu/geoserver/wps"
  raster_layer = "rrk:frid_timesincelastfire_2022_202401_t1_v5"
  vector_layer = "boundary:rrk_boundaries"
  
  # Connect to WFS to get vector geometry
  wfs_url = "https://sparcal.sdsc.edu/geoserver/boundary/wfs"
  wfs_request = f"{wfs_url}?service=WFS&version=1.0.0&request=GetFeature&typeName={vector_layer}&outputFormat=application/json"
  gdf = gpd.read_file(wfs_request)
  geometry = gdf.geometry.iloc[0].wkt  # Use first geometry as WKT
  
  # Connect to WPS
  wps = WebProcessingService(wps_url, version='1.0.0')
  
  # Define inputs for RasterZonalStatistics
  inputs = [
      ('data', raster_layer),  # Raster layer
      ('zones', vector_layer),  # Vector layer
      ('zoneField', 'id'),  # Field to identify zones (assumed field name)
      ('stats', 'Mean,Sum')  # Statistics to compute
  ]
  
  # Execute RasterZonalStatistics process
  execution = wps.execute('gs:RasterZonalStatistics', inputs, output=[('result', 'application/json')])
  
  # Wait for process to complete
  execution.wait()
  
  # Parse and display results
  output = execution.processOutputs[0].retrieveData()
  stats = json.loads(output.decode('utf-8'))
  print("Zonal Statistics:", stats)`}
            language={'python'}
            showLineNumbers={false}
            theme={accessibleCodeTheme}
            wrapLines
            customStyle={{ background: '#ffffff', borderRadius: '4px', padding: '1em' }}
          />
        </div>
        {/* add image */}
        <strong>Conclusion</strong>
        <br />
        This guide has explored how to effectively use the California Wildfire
        Commons' WMS, WCS, WFS and WPS services in ArcGIS, Python, and
        JavaScript-based web mapping frameworks. By combining live service
        integration with offline processing of GeoTIFF and GeoJSON files, users
        can ensure seamless interoperability across platforms for visualizing,
        analyzing, and sharing geospatial data. These methods enhance
        collaboration and optimize the use of the Commons’ resources across
        various workflows.
        </li>
      </ol>
    </div>
  );
};
export { GeoserverInfoView };
